import React, { memo, useEffect, useMemo, useState } from 'react';
import { Button, Card, Icon, Row, Skeleton, Tooltip } from 'antd';
import { convertImage } from '../../../../../services/contents';
import { useTranslation } from 'react-i18next';
import OptionModal from '../../../../shared/components/OptionModal';
import ContentSpecificationAlert from './ContentSpecificationAlert';
import HideWhenCampaignActive from '../../containers/HideWhenCampaignActiveContainer';
import AspectRatioScreenContent from '../../../../shared/components/AspectRatioScreenContent';
import Colors from '../../../../../themes/Colors';

const RequiredResolutionsCard = ({
  removeRelationsOnCampaign,
  customContents,
  setListResolutionWithContent,
  listResolutionWithContent,
  relation,
  loading,
  content = null,
  className,
  style,
  isWarning,
  onClickImage,
  diseabledDeleteButton,
  setListOfContentsInCampaign,
  listOfContentsInCampaign,
  updateImpressionsDisplay,
  groupedDisplays,
  fromModal,
  editCampaignActive,
  currentCampaign,
  isPaymentStatusApproved
}) => {
  const { t } = useTranslation();
  const [countContentsDisplay, setContentsDisplay] = useState(0);
  const [selectedContent, setSelectedContent] = useState(null);
  const [visibleModalDelete, setVisibleModalDelete] = useState(false);

  const otherListRelations = useMemo(() => {
    if (!listResolutionWithContent) return [];
    return listResolutionWithContent.filter(
      group =>
        group.resolution_height !== relation.resolution_height ||
        group.resolution_width !== relation.resolution_width
    );
  }, [listResolutionWithContent]);

  const isInactive = useMemo(() => {
    if (!content || !customContents || !relation || !relation.displays) return false;
    const relationDisplayIds = relation.displays.map(d => d.id);
    const linked = customContents.filter(
      cc => cc.content && cc.content.id === content.id && relationDisplayIds.includes(cc.displayId)
    );
    if (linked.length === 0) return false;
    return linked.every(cc => cc.active === 0);
  }, [content, customContents, relation]);

  const handleDelete = () => {
    let newRelation = { ...relation };

    if (customContents && customContents.length > 0) {
      newRelation.contentsDisplays.forEach(newCustomContent => {
        if (selectedContent !== newCustomContent.contentId) return;

        const contentDisplayForUpdateImpression = customContents.filter(
          item => item.displayId === newCustomContent.displayId
        );
        const deletedCustomContent = contentDisplayForUpdateImpression.find(
          item => item.content.id === newCustomContent.contentId
        );

        if (deletedCustomContent) {
          removeRelationsOnCampaign(
            deletedCustomContent.displayId,
            deletedCustomContent.content.id
          );
          const countCustomContents = contentDisplayForUpdateImpression.length - 1;
          const impressions = Math.trunc(deletedCustomContent.promised_shows / countCustomContents);
          contentDisplayForUpdateImpression.forEach(item => {
            updateImpressionsDisplay(
              item.displayId,
              item.relationId,
              item.promised_shows + impressions
            );
          });
        }
      });
      setSelectedContent(null);
    }

    if (groupedDisplays) {
      const listDisplay = groupedDisplays.map(d => d.id);
      newRelation.contentsDisplays = newRelation.contentsDisplays.filter(
        item => !(item.contentId === selectedContent && listDisplay[0] === item.displayId)
      );
    } else {
      newRelation.contentsDisplays = newRelation.contentsDisplays.filter(
        item => item.contentId !== selectedContent
      );
    }

    newRelation.contents = newRelation.contents.filter(item => item.id !== selectedContent);

    const newListResolutionWithContent = listResolutionWithContent.map(item =>
      item.resolution_height === newRelation.resolution_height &&
      item.resolution_width === newRelation.resolution_width
        ? newRelation
        : item
    );

    const isInOtherRelation = checkIfContentIsInRelation();
    if (!isInOtherRelation) {
      setListOfContentsInCampaign(
        listOfContentsInCampaign.filter(item => item.id !== selectedContent) || []
      );
    }
    setListResolutionWithContent(newListResolutionWithContent);
  };

  const titleForDeleteContent = () => {
    if (!selectedContent) return false;
    const isInOtherRelation = checkIfContentIsInRelation();
    let msg = '';

    if (!isInOtherRelation) {
      if (groupedDisplays) {
        if (fromModal === 'display') {
          msg = t(
            'The ad will be unlinked from this screen. This ad is not linked to any other screen, so it will no longer be used in the campaign. You can add it again later.'
          );
        } else {
          msg = t(
            'The ad will be unlinked from this group of screens. This ad is not linked to any other screen, so it will no longer be used in the campaign. You can add it again later.'
          );
        }
      }
      msg =
        msg ||
        t(
          'The ad will be unlinked from the group of screens for this resolution. Since it is not linked to any other screen, it will no longer be used in the campaign. You can add it again later.'
        );
      return msg;
    }

    if (groupedDisplays) {
      if (fromModal === 'display') {
        msg = t('The ad will be unlinked from this screen. You can add it again later.');
      } else {
        msg = t('The ad will be unlinked from this group of screens. You can add it again later.');
      }
      return msg;
    }

    return t(
      'The ad will be detached from the group of screens of this resolution and will no longer be used in the campaign. You can add it again later.'
    );
  };

  const checkIfContentIsInRelation = () => {
    if (groupedDisplays && groupedDisplays.length === 1) {
      return listResolutionWithContent.some(r =>
        r.contentsDisplays.some(
          cd => cd.contentId === selectedContent && cd.displayId !== groupedDisplays[0].id
        )
      )
        ? true
        : false;
    }
    return otherListRelations.some(r =>
      r.contentsDisplays.some(cd => cd.contentId === selectedContent)
    )
      ? true
      : false;
  };

  const countContentDisplays = () => {
    if (relation && relation.contentsDisplays) {
      const countAux = content
        ? new Set(
            relation.contentsDisplays
              .filter(item => item.contentId === content.id)
              .map(item => item.displayId)
          ).size
        : 0;
      setContentsDisplay(countAux);
    }
  };

  useEffect(() => {
    countContentDisplays();
  }, [relation, customContents]);

  if (countContentsDisplay === 0 && content) return null;

  return (
    <div className={`card-wrapper ${className}`} style={style}>
      {loading ? (
        <Skeleton className="skeletonRequiredCard" active paragraph={{ rows: 1, width: 156 }} />
      ) : (
        <Tooltip
          title={isInactive ? t('This ad version is inactive and cannot be replaced') : ''}
          placement="top"
        >
          <div className={`cardWrapper ${isInactive ? 'card-inactive' : ''}`}>
            <Card className={`cardContent ${isWarning ? 'border-orange' : ''}`}>
              <Row type="flex" style={{ flexDirection: 'column', gap: 10 }} justify="space-between">
                <span className="textCard contentTitleRelation">
                  <p
                    className="textCard centerCard"
                    style={{ fontSize: '12px', marginRight: '4px' }}
                  >
                    {relation.resolution_width} x {relation.resolution_height}
                  </p>
                  {isWarning ? (
                    <Icon type="warning" style={{ marginTop: '5px', color: Colors.atention }} />
                  ) : (
                    <Icon
                      type="desktop"
                      style={{ marginTop: '5px', color: content ? '#7CB354' : '#000000' }}
                    />
                  )}
                  <div style={{ padding: '1px' }}>
                    <ContentSpecificationAlert
                      listResolutionWithContent={[relation]}
                      styleCustom={{ marginBottom: '5px', color: Colors.red }}
                    />
                  </div>
                  <Tooltip title={t('Displays added in this relation')}>
                    <div className={countContentsDisplay ? 'greenCircle' : 'circle'}>
                      {countContentsDisplay || relation.displays.length}
                    </div>
                  </Tooltip>
                </span>
                {!content ? (
                  <AspectRatioScreenContent className="rectangleNoContent" relation={relation} />
                ) : (
                  <>
                    <AspectRatioScreenContent relation={relation}>
                      <img
                        src={convertImage(content.file_thumb)}
                        className="contentRectangleCardImage"
                        onClick={onClickImage}
                        alt=""
                      />
                    </AspectRatioScreenContent>
                    <div className="contentCardContent contentButtonCardResolution">
                      {(!editCampaignActive ||
                        (editCampaignActive && currentCampaign.status !== 'proposal')) &&
                      !(content.isPlaceholder || content.isPendingContent) ? (
                        <span className="primaryCircle" style={{ marginTop: '4px' }}>
                          {content.content_version_name || 'V1'}
                        </span>
                      ) : (
                        <div></div>
                      )}
                      <HideWhenCampaignActive contentId={content.id} displaysId={relation.displays}>
                        {!isPaymentStatusApproved && (
                          <Tooltip
                            title={
                              diseabledDeleteButton
                                ? t(
                                    "It's the last relation on a screen, to delete it, its contents should be reorganized"
                                  )
                                : t('Unlink ad from this screen group.')
                            }
                          >
                            <Button
                              shape="circle"
                              className={!diseabledDeleteButton ? 'icon-card cardRed' : ''}
                              disabled={diseabledDeleteButton}
                              onClick={() => {
                                setSelectedContent(content.id);
                                setVisibleModalDelete(true);
                              }}
                            >
                              <Icon type="close" />
                            </Button>
                          </Tooltip>
                        )}
                      </HideWhenCampaignActive>
                    </div>
                  </>
                )}
              </Row>
            </Card>

            {listResolutionWithContent && selectedContent && (
              <OptionModal
                title={t('Atention!')}
                body={titleForDeleteContent()}
                visible={visibleModalDelete}
                okText={t('Continue')}
                cancelText={t('Cancel')}
                onOk={handleDelete}
                onCancel={() => setVisibleModalDelete(false)}
                afterClose={() => setSelectedContent(null)}
              />
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(RequiredResolutionsCard);
